import { useEffect, useState } from 'react';

// Light, dark or the system's choice. Stored as a cookie on .outlevo.com so outlevo.com and the app share it;
// index.html applies it before the first paint.

export type Theme = 'light' | 'dark' | null;

export const currentTheme = (): Theme => (document.documentElement.getAttribute('data-theme') as Theme) ?? null;

/** What is on screen: the chosen theme, or the system's. */
export const resolvedTheme = (): 'light' | 'dark' =>
  currentTheme() ?? (window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');

export function setTheme(t: Theme) {
  const root = document.documentElement;
  if (t) root.setAttribute('data-theme', t); else root.removeAttribute('data-theme');
  const domain = /(^|\.)outlevo\.com$/.test(location.hostname) ? '; domain=.outlevo.com' : '';
  document.cookie = `olv_theme=${t ?? ''}; path=/; samesite=lax${domain}${t ? '; max-age=31536000' : '; max-age=0'}`;
  window.dispatchEvent(new Event('olv-theme'));
}

export function useResolvedTheme() {
  const [t, set] = useState(resolvedTheme);
  useEffect(() => {
    const on = () => set(resolvedTheme());
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
    window.addEventListener('olv-theme', on);
    mq?.addEventListener?.('change', on);
    return () => { window.removeEventListener('olv-theme', on); mq?.removeEventListener?.('change', on); };
  }, []);
  return t;
}

const LABEL = { light: 'Light', dark: 'Dark', system: 'System' } as const;

/** System → light → dark. */
export function ThemeToggle() {
  const [t, set] = useState<Theme>(currentTheme);
  const next = () => { const n: Theme = t === null ? 'light' : t === 'light' ? 'dark' : null; setTheme(n); set(n); };
  const label = `Theme: ${LABEL[t ?? 'system']}`;
  return (
    <button type="button" className="theme-toggle" onClick={next} aria-label={`${label}. Change theme`} title={label}>
      {t === 'light' ? (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true"><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" /></svg>
      ) : t === 'dark' ? (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" aria-hidden="true"><path d="M20 14.5A8 8 0 1 1 9.5 4a6.5 6.5 0 0 0 10.5 10.5z" /></svg>
      ) : (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><circle cx="12" cy="12" r="8" /><path d="M12 4a8 8 0 0 0 0 16z" fill="currentColor" /></svg>
      )}
    </button>
  );
}
